(function () {
  'use strict';

  /**
   * 集牛前端埋点公共核心。
   *
   * 职责：
   * 1. 生成所有事件共用的页面、用户、会话和 UTM 属性。
   * 2. 读取元素上的 data-jiniu-property-* 与 data-jiniu-properties。
   * 3. 通过 Shopify Web Pixels 发布统一的 jiniu:custom 事件。
   * 4. 自动处理带 data-jiniu-event 的普通点击元素。
   *
   * Shopify 自定义事件协议：
   * {
   *   eventType: 'click' | 'custom',
   *   eventName: '业务事件名',
   *   properties: { ...业务属性, ...公共属性 }
   * }
   *
   * 对外接口：
   * window.jiniuTracking.publish(eventName, properties, eventType)
   * - eventType 省略时默认为 custom。
   * - click 用于用户点击；custom 用于曝光、提交、状态变化等非点击事件。
   *
   * 加载顺序：
   * 本文件必须先于依赖 window.jiniuTracking 的业务埋点脚本加载。
   */
  const CUSTOM_EVENT_NAME = 'jiniu:custom';
  const SESSION_SYNC_EVENT_NAME = 'jiniu:session';
  const VISIT_SYNC_EVENT_NAME = 'jiniu:visit_sync';
  const CLICK_EVENT_TYPE = 'click';
  const CUSTOM_EVENT_TYPE = 'custom';

  // 声明式埋点属性。子元素属性会覆盖父元素上的同名属性。
  const EVENT_ATTRIBUTE = 'data-jiniu-event';
  const PROPERTY_DATASET_PREFIX = 'jiniuProperty';
  const STRUCTURED_PROPERTIES_ATTRIBUTE = 'data-jiniu-properties';

  // 全站公共业务信息。
  const STORE_ID = 'SPELAB';
  const SITE_DOMAIN = 'https://www.spelabautoparts.com/';
  const JN_EVENT_API = 'https://data.jiniu-saas.com/jn-event';

  // visit_id 长期保留；session_id 使用后端返回的 canonical ID。
  const VISIT_ID_KEY = '_jiniu_visit_id';
  const SESSION_ID_KEY = '_jiniu_session_id';
  const CONTEXT_ID_KEY = '_jiniu_context_id';
  const PAY_ID_KEY = '_jiniu_pay_id';
  const UTM_STORAGE_KEY = '_jiniu_utm_params';
  const PIXEL_COMMON_PROPERTIES_KEY = '_jiniu_common_properties';
  const UTM_PROPERTY_NAMES = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  let memoryVisitId = '';
  let publishedVisitId = '';
  let memorySession = null;
  let memoryUtmProperties = null;
  let sessionRequest = null;
  let sessionRetryTimerId = null;
  let nextSessionRetryAt = 0;
  let sessionInitialized = false;
  let sessionNeedsRefresh = false;
  let pageLifecycle = null;
  let pageUnloading = false;
  let pageTransitionVersion = 0;
  let pageTransitionInProgress = false;

  // 神策 local_time 统一使用 UTC-8，不依赖访客浏览器所在时区。
  function getUTCMinus8Time() {
    const now = new Date();
  
    // 转成 UTC-8
    const targetTime = new Date(now.getTime() - 8 * 60 * 60 * 1000);
  
    const YYYY = targetTime.getUTCFullYear();
    const MM = String(targetTime.getUTCMonth() + 1).padStart(2, '0');
    const DD = String(targetTime.getUTCDate()).padStart(2, '0');
    const HH = String(targetTime.getUTCHours()).padStart(2, '0');
    const mm = String(targetTime.getUTCMinutes()).padStart(2, '0');
    const ss = String(targetTime.getUTCSeconds()).padStart(2, '0');
  
    return `${YYYY}-${MM}-${DD} ${HH}:${mm}:${ss}`;
  }

  function generateUUID() {
    const cryptoApi = window.crypto;

    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID();
    }

    const bytes = new Uint8Array(16);

    if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
      cryptoApi.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));

    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join('')
    ].join('-');
  }

  function getStoredValue(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setStoredValue(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function getSessionStoredValue(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function removeStoredValue(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // localStorage may be unavailable in privacy-restricted browsers.
    }
  }

  function getVisitId() {
    const storedVisitId = getStoredValue(VISIT_ID_KEY);

    if (storedVisitId && storedVisitId.trim()) {
      adoptVisitId(storedVisitId);
      publishVisitId(memoryVisitId);
      return memoryVisitId;
    }

    if (!memoryVisitId) {
      adoptVisitId(generateUUID());
    }

    setStoredValue(VISIT_ID_KEY, memoryVisitId);
    publishVisitId(memoryVisitId);
    return memoryVisitId;
  }

  function publishVisitId(visitId) {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish === 'function' && publishedVisitId !== visitId) {
      publish.call(window.Shopify.analytics, VISIT_SYNC_EVENT_NAME, { visit_id: visitId });
      publishedVisitId = visitId;
    }
  }

  function adoptVisitId(visitId) {
    const previousVisitId = memoryVisitId;
    memoryVisitId = visitId;
    if (previousVisitId && previousVisitId !== visitId) {
      const oldPage = pageLifecycle;
      if (oldPage && !oldPage.finished) {
        finishPageState(oldPage);
        pageLifecycle = null;
      }
      memorySession = null;
      sessionInitialized = false;
      sessionNeedsRefresh = true;
      nextSessionRetryAt = 0;
      clearSessionRetryTimer();
      removeStoredValue(SESSION_ID_KEY);
      removeStoredValue(CONTEXT_ID_KEY);
      try {
        window.sessionStorage.removeItem(CONTEXT_ID_KEY);
      } catch (error) {
        // sessionStorage may be unavailable.
      }
      if (window.jiniuTrackingCorrelation) {
        delete window.jiniuTrackingCorrelation.context_id;
      }
      if (oldPage) {
        advancePageViewId();
      }
      if (!pageUnloading) {
        window.setTimeout(recoverSessionAndPage, 0);
      }
    }
  }

  function getSessionId() {
    const storedSession = getStoredValue(SESSION_ID_KEY);

    if (storedSession) {
      try {
        const parsedSession = JSON.parse(storedSession);

        if (
          parsedSession &&
          typeof parsedSession.value === 'string' &&
          parsedSession.value
        ) {
          memorySession = parsedSession;
          return parsedSession.value;
        }
      } catch (error) {
        // Invalid stored data is replaced below.
      }

      removeStoredValue(SESSION_ID_KEY);
    }

    return memorySession?.value || '';
  }

  function getSessionExpiresAt() {
    getSessionId();
    const expiresAt = Number(memorySession?.expiry);
    return Number.isFinite(expiresAt) ? expiresAt : 0;
  }

  function isSessionLocallyExpired() {
    const expiresAt = getSessionExpiresAt();
    return expiresAt <= 0 || expiresAt <= Date.now();
  }

  function createEmptyUtmProperties() {
    return UTM_PROPERTY_NAMES.reduce((properties, propertyName) => {
      properties[propertyName] = '';
      return properties;
    }, {});
  }

  function getCurrentUrlUtmProperties() {
    const searchParts = [window.location.search.slice(1)];
    const hashQueryIndex = window.location.hash.indexOf('?');

    if (hashQueryIndex >= 0) {
      searchParts.push(window.location.hash.slice(hashQueryIndex + 1));
    }

    const urlParameters = new URLSearchParams(searchParts.filter(Boolean).join('&'));
    const properties = createEmptyUtmProperties();
    const foundProperties = new Set();
    let hasUtmParameter = false;

    urlParameters.forEach((value, key) => {
      const propertyName = key.toLowerCase();

      if (
        UTM_PROPERTY_NAMES.includes(propertyName) &&
        !foundProperties.has(propertyName)
      ) {
        properties[propertyName] = value;
        foundProperties.add(propertyName);
        hasUtmParameter = true;
      }
    });

    return { hasUtmParameter, properties };
  }

  function normalizeUtmProperties(value) {
    const properties = createEmptyUtmProperties();

    if (!value || typeof value !== 'object') {
      return properties;
    }

    UTM_PROPERTY_NAMES.forEach((propertyName) => {
      const propertyValue = value[propertyName];
      properties[propertyName] =
        propertyValue === null || propertyValue === undefined
          ? ''
          : String(propertyValue);
    });

    return properties;
  }

  function getUtmProperties() {
    const current = getCurrentUrlUtmProperties();

    if (current.hasUtmParameter) {
      memoryUtmProperties = current.properties;
      setStoredValue(UTM_STORAGE_KEY, JSON.stringify(memoryUtmProperties));
      return memoryUtmProperties;
    }

    if (memoryUtmProperties) {
      return memoryUtmProperties;
    }

    const storedUtmProperties = getStoredValue(UTM_STORAGE_KEY);

    if (storedUtmProperties) {
      try {
        memoryUtmProperties = normalizeUtmProperties(
          JSON.parse(storedUtmProperties)
        );
        return memoryUtmProperties;
      } catch (error) {
        removeStoredValue(UTM_STORAGE_KEY);
      }
    }

    memoryUtmProperties = createEmptyUtmProperties();
    return memoryUtmProperties;
  }

  function getDeviceType() {
    const userAgent = navigator.userAgent || '';
    const isIPadOS =
      /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
    const isAndroidTablet =
      /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
    const isTablet =
      isIPadOS ||
      isAndroidTablet ||
      /iPad|Tablet|PlayBook|Silk|Kindle/i.test(userAgent);

    if (isTablet) {
      return '平板';
    }

    const isMobile =
      /Mobile|Mobi|iPhone|iPod|Windows Phone/i.test(userAgent) ||
      Boolean(navigator.userAgentData?.mobile);

    return isMobile ? '移动设备' : '台式';
  }

  function isExternalEntry() {
    if (!document.referrer) {
      return true;
    }

    try {
      const referrerHost = new URL(document.referrer).hostname.replace(/^www\./, '');
      const currentHost = window.location.hostname.replace(/^www\./, '');
      return (
        referrerHost !== currentHost &&
        !referrerHost.endsWith(`.${currentHost}`) &&
        !currentHost.endsWith(`.${referrerHost}`)
      );
    } catch (error) {
      return false;
    }
  }

  function getSessionCommonProperties() {
    const context = window.jiniuTrackingContext || {};
    return {
      store_id: STORE_ID,
      site_domain: SITE_DOMAIN,
      theme_id: String(context.themeId ?? ''),
      theme_name: context.themeName || '',
      page_type: normalizePageType(context.pageType),
      page_title: document.title,
      page_handle: getPageHandle(),
      user_id: context.userId || '',
      visit_id: getVisitId(),
      ...getIdentityCommonProperties(context),
      device_type: getDeviceType(),
      ...getUtmProperties()
    };
  }

  function getIdentityCommonProperties(context) {
    const visitId = getVisitId();
    const customerId = String(context.userId || '').trim();
    const email = String(context.customerEmail || '').trim();
    const mobile = String(context.customerPhone || '').trim();
    return {
      ...(visitId ? { identity_visit_id: visitId } : {}),
      ...(customerId ? {
        identity_shopify_id: customerId
        // 暂停上报 spelab_shopify_id_list。
      } : {}),
      ...(email && email.length <= 254 ? {
        $identity_email: email
        // 暂停上报 spelab_email_list。
      } : {}),
      ...(mobile && mobile.length <= 32 ? { $identity_mobile: mobile } : {})
    };
  }

  function delay(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function publishSessionSync(sessionId) {
    const publish = window.Shopify?.analytics?.publish;
    const correlation = getCorrelationProperties();
    if (typeof publish === 'function') {
      publish.call(window.Shopify.analytics, SESSION_SYNC_EVENT_NAME, {
        activity_id: generateUUID(),
        activity_at: Date.now(),
        session_id: sessionId,
        ...correlation,
        visit_id: getVisitId(),
        common_properties: getSessionCommonProperties()
      });
    }
    window.dispatchEvent(
      new CustomEvent(SESSION_SYNC_EVENT_NAME, {
        detail: {
          customData: {
            session_id: sessionId,
            visit_id: getVisitId(),
            ...correlation
          }
        }
      })
    );
  }

  function getCorrelationProperties() {
    const shared =
      window.jiniuTrackingCorrelation &&
      typeof window.jiniuTrackingCorrelation === 'object'
        ? window.jiniuTrackingCorrelation
        : {};
    const contextId = String(
      shared.context_id ||
      getSessionStoredValue(CONTEXT_ID_KEY) ||
      getStoredValue(CONTEXT_ID_KEY) ||
      ''
    ).trim();
    const payId = String(
      shared.pay_id ||
      getSessionStoredValue(PAY_ID_KEY) ||
      getStoredValue(PAY_ID_KEY) ||
      ''
    ).trim();
    const cartToken = String(shared.cart_token || '').trim();
    return {
      ...(contextId ? { context_id: contextId } : {}),
      ...(payId ? { pay_id: payId } : {}),
      ...(cartToken ? { cart_token: cartToken } : {})
    };
  }

  async function postSessionStart(attempt, existingBody) {
    const currentUtm = getCurrentUrlUtmProperties();
    const requestVisitId = getVisitId();
    const candidateSessionId = getSessionId();
    if (existingBody && existingBody.visit_id !== requestVisitId) {
      return '';
    }
    const requestBody = existingBody || {
      activity_id: generateUUID(),
      activity_at: Date.now(),
      store_id: STORE_ID,
      shopify_client_id: requestVisitId,
      visit_id: requestVisitId,
      ...getCorrelationProperties(),
      ...(candidateSessionId ? { candidate_session_id: candidateSessionId } : {}),
      distinct_id: requestVisitId,
      surface: 'storefront',
      is_external_entry: isExternalEntry(),
      ...currentUtm.properties,
      common_properties: { ...getSessionCommonProperties(), visit_id: requestVisitId }
    };
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`${JN_EVENT_API}/session/start`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
        keepalive: true
      });

      if (!response.ok) {
        throw new Error(`Session endpoint returned ${response.status}`);
      }

      const result = await response.json();
      const expiresAt = Number(result?.expires_at);
      if (
        !result ||
        typeof result.session_id !== 'string' ||
        !result.session_id ||
        !Number.isFinite(expiresAt) ||
        expiresAt <= 0
      ) {
        throw new Error('Session endpoint returned an invalid response');
      }

      if (requestBody.visit_id !== getVisitId()) {
        sessionNeedsRefresh = true;
        return '';
      }

      memorySession = {
        value: result.session_id,
        expiry: expiresAt
      };
      setStoredValue(SESSION_ID_KEY, JSON.stringify(memorySession));
      if (typeof result.context_id === 'string' && result.context_id) {
        setStoredValue(CONTEXT_ID_KEY, result.context_id);
        window.jiniuTrackingCorrelation = {
          ...(window.jiniuTrackingCorrelation || {}),
          context_id: result.context_id
        };
      }
      persistCommonPropertiesForPixel();
      publishSessionSync(result.session_id);
      return result.session_id;
    } catch (error) {
      if (requestBody.visit_id !== getVisitId()) {
        return '';
      }
      if (attempt >= 3) {
        throw error;
      }
      await delay(250 * 2 ** (attempt - 1));
      return postSessionStart(attempt + 1, requestBody);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function clearSessionRetryTimer() {
    if (sessionRetryTimerId !== null) {
      window.clearTimeout(sessionRetryTimerId);
      sessionRetryTimerId = null;
    }
  }

  function scheduleSessionRetry() {
    if (pageUnloading || sessionRetryTimerId !== null) {
      return;
    }
    const delayMs = Math.max(nextSessionRetryAt - Date.now(), 0);
    sessionRetryTimerId = window.setTimeout(() => {
      sessionRetryTimerId = null;
      if (!pageUnloading) {
        recoverSessionAndPage();
      }
    }, delayMs);
  }

  function ensureCanonicalSession() {
    const currentSessionId = getSessionId();
    const needsRequest =
      !sessionInitialized ||
      sessionNeedsRefresh ||
      isSessionLocallyExpired();
    if (!needsRequest) {
      return Promise.resolve(currentSessionId);
    }
    if (sessionRequest) {
      return sessionRequest;
    }
    if (Date.now() < nextSessionRetryAt) {
      scheduleSessionRetry();
      return Promise.resolve(sessionInitialized ? currentSessionId : '');
    }
    const wasInitialized = sessionInitialized;
    sessionRequest = postSessionStart(1)
      .then((sessionId) => {
        sessionInitialized = Boolean(sessionId);
        sessionNeedsRefresh = !sessionId;
        nextSessionRetryAt = 0;
        clearSessionRetryTimer();
        return sessionId;
      })
      .catch(() => {
        sessionNeedsRefresh = true;
        nextSessionRetryAt = Date.now() + 30_000;
        scheduleSessionRetry();
        return wasInitialized ? currentSessionId : '';
      })
      .finally(() => {
        sessionRequest = null;
        if (sessionNeedsRefresh && !pageUnloading) {
          window.setTimeout(recoverSessionAndPage, 0);
        }
      });
    return sessionRequest;
  }

  function getRouteKey() {
    const hash = window.location.hash;
    const spaHash = /^#(?:\/|!\/)/.test(hash) ? hash.split('?')[0] : '';

    return `${window.location.pathname}${spaHash}`;
  }

  let pageViewId = String(
    window.jiniuTrackingContext?.pageViewId ||
    generateUUID()
  );
  let currentRouteKey = getRouteKey();

  function updateActiveDuration(state) {
    if (state.visibleSince !== null) {
      state.activeMilliseconds += performance.now() - state.visibleSince;
      state.visibleSince = performance.now();
    }
  }

  function pagePayload(state) {
    updateActiveDuration(state);
    state.seq += 1;
    return {
      session_id: state.sessionId,
      page_view_id: state.pageViewId,
      active_duration: state.activeMilliseconds / 1000,
      max_scroll_depth: state.maxScrollDepth,
      seq: state.seq,
      ...getCorrelationProperties(),
      ...(state.lastScrollActivityAt > state.acknowledgedScrollActivityAt
        ? { last_scroll_activity_at: state.lastScrollActivityAt }
        : {}),
      distinct_id: state.visitId,
      common_properties: {
        ...getSessionCommonProperties(),
        visit_id: state.visitId,
        page_view_id: state.pageViewId
      }
    };
  }

  function sendPageCheckpoint() {
    const state = pageLifecycle;
    if (!state || state.finished) {
      return;
    }
    const payload = pagePayload(state);
    if (state.finished) {
      return;
    }

    fetch(`${JN_EVENT_API}/page/checkpoint`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    })
      .then((response) => {
        if (!response.ok) {
          return undefined;
        }
        return response.json();
      })
      .then((result) => {
        if (
          result?.accepted === true &&
          payload.last_scroll_activity_at !== undefined
        ) {
          state.acknowledgedScrollActivityAt = Math.max(
            state.acknowledgedScrollActivityAt,
            payload.last_scroll_activity_at
          );
        }
        if (
          result?.accepted === false &&
          pageLifecycle === state &&
          !state.finished
        ) {
          sessionNeedsRefresh = true;
          retirePageState(state);
          advancePageViewId();
          if (payload.last_scroll_activity_at !== undefined) {
            recoverSessionAndPage();
          }
        }
      })
      .catch(() => undefined);
  }

  function finishPageState(state) {
    if (!state || state.finished) {
      return;
    }

    state.finished = true;
    if (state.timerId !== null) {
      window.clearInterval(state.timerId);
      state.timerId = null;
    }
    const body = JSON.stringify(pagePayload(state));
    state.visibleSince = null;
    let sent = false;
    try {
      sent = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(
          `${JN_EVENT_API}/page/finish`,
          body
        );
    } catch (error) {
      sent = false;
    }

    if (!sent) {
      fetch(`${JN_EVENT_API}/page/finish`, {
        method: 'POST',
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body,
        credentials: 'omit',
        keepalive: true
      }).catch(() => undefined);
    }
  }

  function finishCurrentPage() {
    finishPageState(pageLifecycle);
  }

  function stopPageTimer(state) {
    if (state.timerId !== null) {
      window.clearInterval(state.timerId);
      state.timerId = null;
    }
  }

  function retirePageState(state) {
    if (!state || state.finished) {
      return;
    }
    updateActiveDuration(state);
    state.visibleSince = null;
    stopPageTimer(state);
    state.finished = true;
    if (pageLifecycle === state) {
      pageLifecycle = null;
    }
  }

  function detachPageForBoundary() {
    const state = pageLifecycle;
    if (!state || state.finished) {
      pageLifecycle = null;
      return null;
    }
    updateActiveDuration(state);
    state.visibleSince = null;
    stopPageTimer(state);
    pageLifecycle = null;
    return state;
  }

  function advancePageViewId() {
    pageViewId = generateUUID();
    persistCommonPropertiesForPixel();
  }

  function bindPageLifecycle(sessionId) {
    if (!sessionId || pageUnloading || pageTransitionInProgress) {
      return;
    }
    const state = pageLifecycle;
    if (state && !state.finished && state.sessionId !== sessionId) {
      retirePageState(state);
      advancePageViewId();
    }
    if (!pageLifecycle || pageLifecycle.finished) {
      pageLifecycle = null;
      startPageLifecycle(sessionId);
      return;
    }
    resumeCurrentPage();
  }

  function pauseCurrentPage() {
    const state = pageLifecycle;
    if (!state || state.finished) {
      return;
    }
    updateActiveDuration(state);
    state.visibleSince = null;
    if (state.timerId !== null) {
      window.clearInterval(state.timerId);
      state.timerId = null;
    }
    sendPageCheckpoint();
  }

  function resumeCurrentPage() {
    const state = pageLifecycle;
    if (!state || state.finished || document.visibilityState !== 'visible') {
      return;
    }
    if (state.visibleSince === null) {
      state.visibleSince = performance.now();
    }
    if (state.timerId === null) {
      state.timerId = window.setInterval(sendPageCheckpoint, 5000);
    }
  }

  function recoverSessionAndPage() {
    ensureCanonicalSession()
      .then((sessionId) => {
        if (!sessionId) {
          return;
        }
        bindPageLifecycle(sessionId);
      })
      .catch(() => undefined);
  }

  function startPageLifecycle(sessionId) {
    const documentHeight = Math.max(document.documentElement.scrollHeight, 1);
    pageLifecycle = {
      sessionId,
      visitId: getVisitId(),
      pageViewId,
      activeMilliseconds: 0,
      visibleSince: document.visibilityState === 'visible' ? performance.now() : null,
      maxScrollDepth: Math.min(
        100,
        Math.max(0, ((window.scrollY + window.innerHeight) / documentHeight) * 100)
      ),
      lastScrollActivityAt: 0,
      acknowledgedScrollActivityAt: 0,
      seq: 0,
      timerId: null,
      finished: false
    };
    if (document.visibilityState === 'visible') {
      pageLifecycle.timerId = window.setInterval(sendPageCheckpoint, 5000);
    }
  }

  function observePageActivity() {
    document.addEventListener('visibilitychange', () => {
      const state = pageLifecycle;
      if (document.visibilityState === 'hidden') {
        if (state && !state.finished) {
          pauseCurrentPage();
        }
      } else {
        if (state && !state.finished) {
          resumeCurrentPage();
        }
        recoverSessionAndPage();
      }
    });

    let userScrollIntentUntil = 0;
    const markUserScrollIntent = (durationMilliseconds = 1500) => {
      userScrollIntentUntil = Math.max(
        userScrollIntentUntil,
        performance.now() + durationMilliseconds
      );
    };
    window.addEventListener('wheel', (event) => {
      if (event.isTrusted) {
        markUserScrollIntent();
      }
    }, { passive: true });
    window.addEventListener('touchmove', (event) => {
      if (event.isTrusted) {
        markUserScrollIntent();
      }
    }, { passive: true });
    document.addEventListener('keydown', (event) => {
      if (
        event.isTrusted &&
        [
          'ArrowUp',
          'ArrowDown',
          'PageUp',
          'PageDown',
          'Home',
          'End',
          ' ',
          'Spacebar'
        ].includes(event.key)
      ) {
        markUserScrollIntent();
      }
    });
    window.addEventListener('pointerdown', (event) => {
      const documentElement = document.documentElement;
      const isScrollbarInteraction =
        event.button === 1 ||
        event.clientX >= documentElement.clientWidth ||
        event.clientY >= documentElement.clientHeight;
      if (event.isTrusted && isScrollbarInteraction) {
        markUserScrollIntent(5000);
      }
    }, { passive: true });

    let scrollScheduled = false;
    window.addEventListener('scroll', () => {
      if (scrollScheduled) {
        return;
      }
      scrollScheduled = true;
      window.requestAnimationFrame(() => {
        scrollScheduled = false;
        const state = pageLifecycle;
        const isUserScroll = performance.now() <= userScrollIntentUntil;
        if (!state || state.finished) {
          if (isUserScroll && !pageUnloading) {
            recoverSessionAndPage();
          }
          return;
        }
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          1
        );
        const depth = Math.min(
          100,
          Math.max(
            0,
            ((window.scrollY + window.innerHeight) / documentHeight) * 100
          )
        );
        state.maxScrollDepth = Math.max(state.maxScrollDepth, depth);
        if (isUserScroll) {
          state.lastScrollActivityAt = Date.now();
        }
      });
    }, { passive: true });

    window.addEventListener('pagehide', () => {
      pageUnloading = true;
      clearSessionRetryTimer();
      finishCurrentPage();
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        pageUnloading = false;
        advancePageViewId();
        recoverSessionAndPage();
      }
    });
  }

  function handlePageBoundary() {
    const transitionVersion = pageTransitionVersion + 1;
    pageTransitionVersion = transitionVersion;
    pageTransitionInProgress = true;
    const previousPage = detachPageForBoundary();
    const previousSessionId = previousPage?.sessionId || '';
    advancePageViewId();

    ensureCanonicalSession()
      .then((sessionId) => {
        if (previousPage && !previousPage.finished) {
          if (sessionId && sessionId !== previousSessionId) {
            retirePageState(previousPage);
          } else {
            finishPageState(previousPage);
          }
        }
        if (transitionVersion !== pageTransitionVersion) {
          return;
        }
        pageTransitionInProgress = false;
        if (sessionId) {
          bindPageLifecycle(sessionId);
        }
      })
      .catch(() => {
        if (previousPage && !previousPage.finished) {
          finishPageState(previousPage);
        }
        if (transitionVersion === pageTransitionVersion) {
          pageTransitionInProgress = false;
        }
      });
  }

  // 兼容 history API 和 hash 路由；页面路由变化时刷新 page_view_id。
  function refreshPageViewId(forceRefresh) {
    const nextRouteKey = getRouteKey();

    if (forceRefresh || nextRouteKey !== currentRouteKey) {
      handlePageBoundary();
    }

    currentRouteKey = nextRouteKey;
    persistCommonPropertiesForPixel();
  }

  function observePageViewChanges() {
    ['pushState', 'replaceState'].forEach((methodName) => {
      const originalMethod = window.history[methodName];

      if (typeof originalMethod !== 'function') {
        return;
      }

      window.history[methodName] = function () {
        const result = originalMethod.apply(this, arguments);
        refreshPageViewId(false);
        return result;
      };
    });

    window.addEventListener('popstate', () => refreshPageViewId(true));
    window.addEventListener('hashchange', () => refreshPageViewId(false));
  }

  function normalizePageType(pageType) {
    if (pageType === 'index') {
      return 'home';
    }

    if (
      ['product', 'collection', 'search', 'article', 'cart', 'page'].includes(
        pageType
      )
    ) {
      return pageType;
    }

    return 'other';
  }

  function getPageHandle() {
    return window.location.pathname.replace(/^\/+/, '');
  }

  function persistCommonPropertiesForPixel() {
    const context = window.jiniuTrackingContext || {};

    setStoredValue(
      PIXEL_COMMON_PROPERTIES_KEY,
      JSON.stringify({
        href: window.location.href,
        properties: {
          page_view_id: pageViewId,
          theme_id: context.themeId ?? '',
          theme_name: context.themeName || '',
          page_type: normalizePageType(context.pageType),
          user_id: context.userId || ''
        }
      })
    );
  }

  // 所有业务事件都会在发布前合并这些公共属性。
  function getCommonProperties() {
    const context = window.jiniuTrackingContext || {};

    return {
      jn_event_id: generateUUID(),
      local_time: getUTCMinus8Time(),
      page_view_id: pageViewId,
      store_id: STORE_ID,
      site_domain: SITE_DOMAIN,
      theme_id: context.themeId ?? '',
      theme_name: context.themeName || '',
      page_type: normalizePageType(context.pageType),
      page_title: document.title,
      page_handle: getPageHandle(),
      user_id: context.userId || '',
      visit_id: getVisitId(),
      ...getIdentityCommonProperties(context),
      session_id: getSessionId(),
      device_type: getDeviceType(),
      ...getUtmProperties()
    };
  }

  function toPropertyName(datasetKey) {
    return datasetKey
      .slice(PROPERTY_DATASET_PREFIX.length)
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase();
  }

  // 把 data-jiniu-property-product-id 转换为 product_id。
  function getProperties(element) {
    const properties = {};

    Object.entries(element.dataset).forEach(([key, value]) => {
      if (
        !key.startsWith(PROPERTY_DATASET_PREFIX) ||
        key === 'jiniuProperties'
      ) {
        return;
      }

      const propertyName = toPropertyName(key);

      if (propertyName) {
        properties[propertyName] = value;
      }
    });

    return properties;
  }

  // data-jiniu-properties 用于承载不适合拆成多个 HTML 属性的 JSON 数据。
  function getStructuredProperties(element) {
    const value = element.getAttribute(STRUCTURED_PROPERTIES_ATTRIBUTE);

    if (!value) {
      return {};
    }

    try {
      const properties = JSON.parse(value);
      return properties && typeof properties === 'object' ? properties : {};
    } catch (error) {
      return {};
    }
  }

  // 从外层容器向点击元素合并属性，越靠近目标元素的值优先级越高。
  function getInheritedProperties(element) {
    const elements = [];
    const properties = {};
    let currentElement = element;

    while (currentElement instanceof Element) {
      elements.push(currentElement);
      currentElement = currentElement.parentElement;
    }

    elements.reverse().forEach((current) => {
      Object.assign(
        properties,
        getStructuredProperties(current),
        getProperties(current)
      );
    });

    return properties;
  }

  function normalizeEventType(eventType) {
    return eventType === CLICK_EVENT_TYPE
      ? CLICK_EVENT_TYPE
      : CUSTOM_EVENT_TYPE;
  }

  // Shopify Pixel 只需订阅这一种自定义事件，再按 eventName 上报神策事件。
  function publishEvent(eventName, properties, eventType) {
    const publish = window.Shopify?.analytics?.publish;

    if (typeof publish !== 'function') {
      return;
    }

    publish.call(window.Shopify.analytics, CUSTOM_EVENT_NAME, {
      eventType: normalizeEventType(eventType),
      eventName,
      properties
    });
  }

  const claimedClickEvents = new Set();

  // 公共属性最后合并，避免业务代码覆盖 event_id、visit_id 等基础字段。
  function publishTrackedEvent(eventName, properties, eventType) {
    if (!eventName) {
      return;
    }

    publishEvent(
      eventName,
      {
        ...(properties || {}),
        ...getCommonProperties()
      },
      eventType
    );
    recoverSessionAndPage();
  }

  /**
   * 业务脚本统一使用的公开接口。
   *
   * claimClickEvent:
   * 标记由专用脚本接管的点击事件，防止声明式监听器重复上报。
   *
   * getInheritedProperties:
   * 读取某个元素及其祖先节点上的声明式属性。
   *
   * publish:
   * 发布任意业务事件；第三个参数仅接受 click，其余值按 custom 处理。
   */
  window.jiniuTracking = Object.freeze({
    claimClickEvent(eventName) {
      if (eventName) {
        claimedClickEvents.add(eventName);
      }
    },
    getInheritedProperties,
    publish(eventName, properties, eventType = CUSTOM_EVENT_TYPE) {
      publishTrackedEvent(eventName, properties, eventType);
    }
  });

  observePageViewChanges();
  observePageActivity();
  window.addEventListener('storage', (event) => {
    if (event.key === VISIT_ID_KEY && event.newValue && event.newValue !== memoryVisitId) {
      getVisitId();
      recoverSessionAndPage();
    }
  });
  persistCommonPropertiesForPixel();
  recoverSessionAndPage();

  // 通用声明式点击监听；商品点击等复杂事件由专用脚本接管。
  document.addEventListener(
    'click',
    function (event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const target = event.target.closest(`[${EVENT_ATTRIBUTE}]`);

      if (!target) {
        return;
      }

      const eventName = target.getAttribute(EVENT_ATTRIBUTE);

      if (!eventName || claimedClickEvents.has(eventName)) {
        return;
      }

      publishTrackedEvent(
        eventName,
        getInheritedProperties(target),
        CLICK_EVENT_TYPE
      );
    },
    { capture: true }
  );
})();
